export interface BackupData {
  timestamp: string;
  userId: number;
  userEmail: string;
  portfolio: any[];
  payments: any[];
  sellTransactions: any[];
  version: string;
}

class BackupService {
  private readonly BACKUP_KEY = 'finance_app_backups';
  private readonly MAX_BACKUPS = 10; // Son 10 yedek sakla

  // Yedek oluştur
  createBackup(userId: number, userEmail: string, portfolio: any[], payments: any[], sellTransactions: any[]): void {
    const backup: BackupData = {
      timestamp: new Date().toISOString(),
      userId,
      userEmail,
      portfolio,
      payments,
      sellTransactions,
      version: '1.0'
    };

    // Mevcut yedekleri al
    const existingBackups = this.getBackups();
    
    // Yeni yedeği ekle
    existingBackups.push(backup);
    
    // En eski yedekleri sil (max 10 tane tut)
    if (existingBackups.length > this.MAX_BACKUPS) {
      existingBackups.splice(0, existingBackups.length - this.MAX_BACKUPS);
    }
    
    // Kaydet
    localStorage.setItem(this.BACKUP_KEY, JSON.stringify(existingBackups));
  }

  // Tüm yedekleri al
  getBackups(): BackupData[] {
    const backupsStr = localStorage.getItem(this.BACKUP_KEY);
    return backupsStr ? JSON.parse(backupsStr) : [];
  }

  // Kullanıcının yedeklerini al
  getUserBackups(userId: number): BackupData[] {
    const allBackups = this.getBackups();
    return allBackups.filter(backup => backup.userId === userId);
  }

  // Yedekten geri yükle
  restoreFromBackup(backup: BackupData): void {
    if (backup.portfolio) {
      localStorage.setItem(`portfolio_${backup.userId}`, JSON.stringify(backup.portfolio));
    }
    if (backup.payments) {
      localStorage.setItem(`payments_${backup.userId}`, JSON.stringify(backup.payments));
    }
    if (backup.sellTransactions) {
      localStorage.setItem(`sellTransactions_${backup.userId}`, JSON.stringify(backup.sellTransactions));
    }
  }

  // Yedeği indir (JSON dosyası olarak)
  downloadBackup(backup: BackupData): void {
    const dataStr = JSON.stringify(backup, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `finance_backup_${new Date(backup.timestamp).toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log(`📁 Backup downloaded: ${link.download}`);
  }

  // JSON dosyasından yedek yükle
  uploadBackup(file: File, userId: number): Promise<BackupData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const backup = JSON.parse(e.target?.result as string);
          
          // Yedeğin formatını kontrol et
          if (!backup.timestamp || !backup.portfolio || !backup.payments || !backup.sellTransactions) {
            throw new Error('Invalid backup format');
          }
          
          // User ID'yi güncelle
          backup.userId = userId;
          
          resolve(backup);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  // Otomatik yedekleme (her gün)
  scheduleAutoBackup(userId: number, userEmail: string, portfolio: any[], payments: any[], sellTransactions: any[]): void {
    const now = new Date();
    const lastBackupStr = localStorage.getItem(`last_auto_backup_${userId}`);
    
    if (lastBackupStr) {
      const lastBackup = new Date(lastBackupStr);
      const daysSinceLastBackup = Math.floor((now.getTime() - lastBackup.getTime()) / (1000 * 60 * 60 * 24));
      
      // Eğer 1 günden az geçtiyse yedekleme
      if (daysSinceLastBackup < 1) {
        return;
      }
    }
    
    // Yedek oluştur
    this.createBackup(userId, userEmail, portfolio, payments, sellTransactions);
    
    // Son yedekleme zamanını kaydet
    localStorage.setItem(`last_auto_backup_${userId}`, now.toISOString());
    
    console.log('🔄 Auto backup completed');
  }

  // Yedekleri temizle (eski olanları)
  cleanupOldBackups(): void {
    const backups = this.getBackups();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const filteredBackups = backups.filter(backup => 
      new Date(backup.timestamp) > thirtyDaysAgo
    );
    
    localStorage.setItem(this.BACKUP_KEY, JSON.stringify(filteredBackups));
    console.log(`🧹 Cleaned up ${backups.length - filteredBackups.length} old backups`);
  }
}

export const backupService = new BackupService();
